import { flatMap, pipe, uniqueBy } from 'remeda'
import type { Cleanup } from './cleanup'

// Private types ---------------------------------------------------------------

type ComponentLoader = () => Promise<unknown>

// Constants -------------------------------------------------------------------

// These controls accept attributes before upgrade; composite hooks wait through setupPageComponent.
const PAGE_COMPONENTS: ReadonlyMap<string, ComponentLoader> = new Map<
  string,
  ComponentLoader
>([
  [
    'wa-breadcrumb',
    () =>
      import(
        '@awesome.me/webawesome-pro/dist/components/breadcrumb/breadcrumb.js'
      )
  ],
  [
    'wa-breadcrumb-item',
    () =>
      import(
        '@awesome.me/webawesome-pro/dist/components/breadcrumb-item/breadcrumb-item.js'
      )
  ],
  [
    'wa-card',
    () => import('@awesome.me/webawesome-pro/dist/components/card/card.js')
  ],
  [
    'wa-carousel',
    () =>
      import('@awesome.me/webawesome-pro/dist/components/carousel/carousel.js')
  ],
  [
    'wa-carousel-item',
    () =>
      import(
        '@awesome.me/webawesome-pro/dist/components/carousel-item/carousel-item.js'
      )
  ],
  [
    'wa-color-picker',
    () =>
      import(
        '@awesome.me/webawesome-pro/dist/components/color-picker/color-picker.js'
      )
  ],
  [
    'wa-copy-button',
    () =>
      import(
        '@awesome.me/webawesome-pro/dist/components/copy-button/copy-button.js'
      )
  ],
  [
    'wa-date-input',
    () =>
      import(
        '@awesome.me/webawesome-pro/dist/components/date-input/date-input.js'
      )
  ],
  [
    'wa-date-picker',
    () =>
      import(
        '@awesome.me/webawesome-pro/dist/components/date-picker/date-picker.js'
      )
  ],
  [
    'wa-number-input',
    () =>
      import(
        '@awesome.me/webawesome-pro/dist/components/number-input/number-input.js'
      )
  ],
  [
    'wa-otp-input',
    () =>
      import(
        '@awesome.me/webawesome-pro/dist/components/otp-input/otp-input.js'
      )
  ],
  [
    'wa-page',
    () => import('@awesome.me/webawesome-pro/dist/components/page/page.js')
  ],
  [
    'wa-pagination',
    () =>
      import(
        '@awesome.me/webawesome-pro/dist/components/pagination/pagination.js'
      )
  ],
  [
    'wa-scroller',
    () =>
      import('@awesome.me/webawesome-pro/dist/components/scroller/scroller.js')
  ],
  [
    'wa-select',
    () => import('@awesome.me/webawesome-pro/dist/components/select/select.js')
  ],
  [
    'wa-slider',
    () => import('@awesome.me/webawesome-pro/dist/components/slider/slider.js')
  ],
  [
    'wa-switch',
    () => import('@awesome.me/webawesome-pro/dist/components/switch/switch.js')
  ],
  [
    'wa-tab-group',
    async () => {
      // The group reads child APIs as soon as its definition upgrades the host.
      await Promise.all([
        import('@awesome.me/webawesome-pro/dist/components/tab/tab.js'),
        import(
          '@awesome.me/webawesome-pro/dist/components/tab-panel/tab-panel.js'
        )
      ])

      return import(
        '@awesome.me/webawesome-pro/dist/components/tab-group/tab-group.js'
      )
    }
  ],
  [
    'wa-tab',
    () => import('@awesome.me/webawesome-pro/dist/components/tab/tab.js')
  ],
  [
    'wa-tab-panel',
    () =>
      import(
        '@awesome.me/webawesome-pro/dist/components/tab-panel/tab-panel.js'
      )
  ],
  [
    'wa-time-input',
    () =>
      import(
        '@awesome.me/webawesome-pro/dist/components/time-input/time-input.js'
      )
  ],
  [
    'wa-tree',
    () => import('@awesome.me/webawesome-pro/dist/components/tree/tree.js')
  ],
  [
    'wa-tree-item',
    () =>
      import(
        '@awesome.me/webawesome-pro/dist/components/tree-item/tree-item.js'
      )
  ],
  [
    'wa-video',
    () => import('@awesome.me/webawesome-pro/dist/components/video/video.js')
  ]
])

const PAGE_COMPONENT_SELECTOR = [...PAGE_COMPONENTS.keys()].join(',')

// Public functions ------------------------------------------------------------

// Loads a server-rendered control as LiveView adds it; the browser shares repeated imports.
export const loadPageComponent = (node: Node): Promise<unknown> | undefined =>
  node instanceof Element && !customElements.get(node.localName)
    ? PAGE_COMPONENTS.get(node.localName)?.()
    : undefined

// Attaches a hook after its control loads, unless the hook has already been destroyed.
export const setupPageComponent = (
  element: HTMLElement,
  setup: (element: HTMLElement) => Cleanup,
  load: (
    element: HTMLElement
  ) => Promise<unknown> | undefined = loadPageComponent
): Cleanup => {
  let active = true
  let cleanup: Cleanup | undefined

  // Keeps setup and its cleanup within the lifetime of this hook instance.
  const attach = (): void => {
    if (active) {
      cleanup = setup(element)
    }
  }
  const loading = load(element)

  if (loading) {
    loading.then(attach).catch((error: unknown) => {
      if (active) {
        console.error(`Unable to initialize ${element.localName}`, error)
      }
    })
  } else {
    attach()
  }

  return () => {
    active = false
    cleanup?.()
  }
}

// Loads missing controls in a page or composite control, including its host.
export const loadPageComponents = (
  root: ParentNode
): Promise<void> | undefined => {
  const elements = [
    ...(root instanceof Element ? [root] : []),
    ...root.querySelectorAll(PAGE_COMPONENT_SELECTOR)
  ]
  const loading = pipe(
    elements,
    uniqueBy((element) => element.localName),
    flatMap((element) => {
      const definition = loadPageComponent(element)

      return definition ? [definition] : []
    })
  )

  return loading.length > 0
    ? Promise.all(loading).then(() => undefined)
    : undefined
}
